// i18n provider + hook (Conv-27, founder-ordered full localisation).
//
// - The English source string is the message key (gettext-style). A missing
//   entry falls back to English, so partial coverage degrades gracefully and a
//   raw key can never render.
// - Active locale precedence: explicit on-device override (localStorage) >
//   the signed-in account's saved pref > English. An override lets a signed-out
//   visitor pick a language on Landing/Auth, and lets a signed-in user switch
//   instantly before the server round-trip lands.
// - Changing locale re-renders every consumer (the whole app), so the toggle
//   switches every translated string at once.
//
// Conv-30 — the catalogs are LAZY (see loadCatalog.ts). English costs zero extra
// bytes; each other locale is its own chunk. Two consequences are load-bearing:
//
//   FIRST PAINT IS GATED, LATER SWITCHES ARE NOT. Before the first paint we hold a
//   text-free splash until the catalog we already know we need has arrived, so a
//   Hindi user never sees a frame of English. After the app has painted we never
//   blank it again — a locale switch keeps rendering the old language until the new
//   catalog lands and then swaps. Blanking a page someone is reading is worse than
//   the flash it would prevent.
//
//   THE SPLASH CARRIES NO TEXT. Anything it said would be a string rendered before
//   its own translation exists, i.e. the exact bug this gate is here to stop.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../state/AuthContext';
import { setDisplayPrefs } from '../lib/format';
import { SUPPORTED_LOCALES, normalizeLocale, type Locale } from './locales';
import {
  cachedCatalog,
  cachedDocs,
  loadCatalog,
  loadDocs,
  onCatalogLate,
  readLocaleHint,
  writeLocaleHint,
  type Catalog,
} from './loadCatalog';

const LS_KEY = 'sansara.locale';

// How long first paint may wait on the session request before giving up and painting.
// Matches the catalog loader's own timeout: both are "the app must appear even when a
// dependency never answers".
const AUTH_WAIT_CAP_MS = 8000;

// t(englishSource, vars?) → translated string with {placeholder} interpolation.
export type TFn = (en: string, vars?: Record<string, string | number>) => string;

interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: TFn;
}

const I18nCtx = createContext<I18nValue>({
  locale: 'en-IN',
  setLocale: () => {},
  t: (en) => en,
});

const interpolate = (s: string, vars?: Record<string, string | number>): string =>
  vars ? s.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m)) : s;

const asLocale = (v: string | null | undefined): Locale | null =>
  v && (SUPPORTED_LOCALES as string[]).includes(v) ? (v as Locale) : null;

const readOverride = (): Locale | null => {
  try {
    return asLocale(localStorage.getItem(LS_KEY));
  } catch {
    return null;
  }
};

// Start the chunk request at MODULE scope, before React has mounted anything. The
// override is authoritative; the hint is the last locale this device resolved and
// merely gets the fetch moving while the session request is still in flight. Both
// are read synchronously, so for any returning non-English user the catalog is
// typically in memory by the time the provider first renders — the gate below then
// costs nothing.
const bootOverride = readOverride();
const bootHint = asLocale(readLocaleHint());
const preloadTarget = bootOverride ?? bootHint;
if (preloadTarget && preloadTarget !== 'en-IN') void loadCatalog(preloadTarget);

// Do we have any reason to believe this device will resolve to a non-English
// locale? Only then is it worth holding first paint for the session request. A
// first-time English visitor — no override, no hint — must paint immediately and
// download zero translation bytes; making everyone wait on an auth probe to protect
// a case we can already rule out would trade one regression for another.
const mayBeNonEnglish = bootOverride ? bootOverride !== 'en-IN' : bootHint !== null && bootHint !== 'en-IN';

export function I18nProvider({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  const [override, setOverride] = useState<Locale | null>(readOverride);

  // Explicit device override wins; otherwise follow the account preference.
  const locale: Locale = override ?? normalizeLocale(session?.preferences?.locale);

  // The RESOLVED catalog, tagged with the locale it was resolved FOR.
  //
  // The tag is the whole point and its absence was a real bug: the first-paint gate
  // used to ask `cachedCatalog(locale) !== null`, but loadCatalog deliberately does
  // NOT cache a failure (so a transient blip can be retried). A chunk 404 — the
  // ordinary consequence of deploying a new dist/ under an open tab — therefore
  // resolved to an empty catalog, left the cache empty, and the gate stayed shut
  // FOREVER on a text-free splash. The loader failed open; the gate failed closed;
  // the two disagreed because they read different sources of truth. Now there is one
  // source: a resolution for THIS locale, successful or not, clears the gate.
  const [loaded, setLoaded] = useState<{ locale: Locale; catalog: Catalog } | null>(() => {
    const hit = cachedCatalog(locale);
    return hit ? { locale, catalog: hit } : null;
  });

  // Once true, we never blank the app again — only swap languages in place.
  const [painted, setPainted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const ready = cachedCatalog(locale);
    if (ready) {
      setLoaded({ locale, catalog: ready });
      return;
    }
    // Not in memory: keep rendering whatever we have (English on a cold start, the
    // previous language on a switch) and swap when it lands. loadCatalog never
    // rejects — a failure resolves to an empty catalog, which records "this locale is
    // settled, it just renders English", and that is exactly what must clear the gate.
    // A catalog that loses the timeout race still arrives; apply it when it does,
    // rather than leaving the session in English with the translations in memory.
    const unsubscribeLate = onCatalogLate(locale, (c) => {
      if (!cancelled) setLoaded({ locale, catalog: c });
    });
    void loadCatalog(locale).then((c) => {
      if (!cancelled) setLoaded({ locale, catalog: c });
    });
    return () => {
      unsubscribeLate();
      // A second switch while the first is in flight: the stale resolution is dropped
      // rather than applied, so A→B→A never lands B's catalog under locale A.
      cancelled = true;
    };
  }, [locale]);

  const catalog = loaded?.catalog ?? null;

  // Settled for the CURRENT locale — not merely "some catalog is in memory".
  const rendered = loaded?.locale === locale;

  // The DOCUMENT catalog (legal documents, Learn articles) — see loadCatalog.ts. It
  // is fetched only AFTER the main catalog has settled for this locale, so it never
  // competes with first paint, and it is tagged with its locale like `loaded` so a
  // late answer for a locale the reader has since left is dropped, not applied.
  const [docs, setDocs] = useState<{ locale: Locale; catalog: Catalog } | null>(null);
  useEffect(() => {
    if (locale === 'en-IN' || !rendered) return;
    let cancelled = false;
    const hit = cachedDocs(locale);
    if (hit) {
      setDocs({ locale, catalog: hit });
      return;
    }
    void loadDocs(locale).then((c) => {
      if (!cancelled) setDocs({ locale, catalog: c });
    });
    return () => {
      cancelled = true;
    };
  }, [locale, rendered]);
  const docCatalog = docs?.locale === locale ? docs.catalog : null;

  // `t` reads these, so they must be current the moment children render — an effect
  // runs too late and would leave the first post-load render showing English with no
  // further re-render scheduled, i.e. permanently English.
  const localeRef = useRef(locale);
  const catalogRef = useRef(catalog);
  const docsRef = useRef(docCatalog);
  localeRef.current = locale;
  catalogRef.current = catalog;
  docsRef.current = docCatalog;

  useEffect(() => {
    // Keep the document language attribute honest for a11y / screen readers — but
    // only once the strings on screen ARE in that language. Flipping it early points
    // a screen reader at a Hindi voice reading English text, which is worse than the
    // stale attribute it replaces.
    //
    // "Resolved" is not enough: a load that failed or timed out resolves to an EMPTY
    // catalog, which is a perfectly good render — of ENGLISH. So the attribute follows
    // whether there are actually translations to read, not merely whether we stopped
    // waiting for them.
    const speakingLocale = locale === 'en-IN' || (rendered && catalog !== null && Object.keys(catalog).length > 0);
    try {
      document.documentElement.lang = speakingLocale ? locale.slice(0, 2) : 'en';
    } catch {
      /* SSR/no-DOM guard — never throws in the browser */
    }
    // Keep the NUMBER/DATE formatter on the same locale as the strings.
    //
    // format.ts's displayLocale is written by AuthContext from the ACCOUNT preference
    // only, so a device override (the thing that wins for every translated string) left
    // dates and grouped numerals following a locale the user is not reading — a Tamil
    // UI printing Hindi month names. The provider owns the resolved locale, so it is
    // the right place to push it.
    setDisplayPrefs({ locale });

    // Record the resolved locale so the NEXT load can start its chunk immediately.
    // Deliberately not the override key: this is a prefetch hint, not a choice.
    if (!loading) writeLocaleHint(locale);
  }, [locale, loading, rendered, catalog]);

  const setLocale = useCallback((l: Locale) => {
    setOverride(l);
    void loadCatalog(l); // start the chunk on click, not on the next render
    try {
      localStorage.setItem(LS_KEY, l);
    } catch {
      /* storage unavailable — the in-memory override still applies this session */
    }
  }, []);

  // `t` NEVER changes identity — it reads the refs above.
  //
  // That is load-bearing, not tidiness. Six pages put `t` in a data-fetch dependency
  // chain (useCallback(reload, [path, t]) → useEffect(reload, [reload]) in
  // Privacy/Desk/Trackers/Community/Learn), so every time `t` got a new identity those
  // pages re-fired real API GETs. Before Conv-30 that happened on each locale switch;
  // with a lazily-arriving catalog it would ALSO fire on every cold load in a
  // non-English locale. A stable `t` removes the whole class.
  //
  // Consumers still re-render when a catalog lands, because the CONTEXT VALUE below
  // changes identity — that is what propagates, and `t` then returns the new strings.
  const t = useCallback<TFn>((en, vars) => {
    if (localeRef.current === 'en-IN') return interpolate(en, vars);
    return interpolate(catalogRef.current?.[en] ?? docsRef.current?.[en] ?? en, vars);
  }, []);

  // `catalog` is in the dep list on purpose although the object does not read it: it
  // is the signal that the strings changed. Dropping it would freeze the app in
  // whatever language it first painted.
  const value = useMemo<I18nValue>(() => ({ locale, setLocale, t }), [locale, catalog, docCatalog, setLocale, t]);

  // Gate the FIRST paint only. Two things can make the locale unknown-or-unloaded:
  // the session (and with it the account preference) is still in flight on a device
  // that has previously resolved to a non-English locale, or the catalog for an
  // already-known non-English locale has not arrived yet. Either way, painting now
  // would paint English and correct itself a moment later — the flash of English the
  // sim personas kept reporting.
  // The auth wait is CAPPED. `loading` clears only when GET /api/auth/me resolves, and
  // that request has no timeout of its own — so an unreachable or wedged API would hold
  // the splash indefinitely on a device that merely MIGHT be non-English. The catalog
  // side already fails open after 8s; this gives the session side the same bound, and
  // when it expires we paint in whatever locale we can determine (the override, else
  // English) and swap in place if the session later says otherwise.
  const [authWaitExpired, setAuthWaitExpired] = useState(false);
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => setAuthWaitExpired(true), AUTH_WAIT_CAP_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  const settling = (loading && !override && mayBeNonEnglish && !authWaitExpired) || !rendered;

  useEffect(() => {
    if (!settling) setPainted(true);
  }, [settling]);

  if (!painted && settling) {
    return (
      <div className="flex min-h-dvh items-center justify-center" aria-busy="true">
        <div className="skeleton h-10 w-10 rounded-full" />
      </div>
    );
  }

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);
export const useT = (): TFn => useContext(I18nCtx).t;
